import { QueryClient } from '@tanstack/react-query';
import { ApiProblem } from '@/lib/api';

/**
 * Freshness classes, DESIGN.md section 6. The defaults here are the live class. A snapshot
 * or model query passes its own staleTime; nothing polls on its own timer except the live
 * class, because /changes is the single source of invalidation.
 */
export const LIVE = { staleTime: 60_000, refetchInterval: 30_000 } as const;
export const SNAPSHOT = { staleTime: 900_000, refetchOnWindowFocus: true } as const;
export const MODEL = { staleTime: Number.POSITIVE_INFINITY, refetchOnWindowFocus: false } as const;

/** Retrying a 403 or a 409 just burns rate limit and confuses the operator. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiProblem) {
    if (error.status === 0) return failureCount < 2;
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: LIVE.staleTime,
        gcTime: 300_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
        // A dead card keeps its last numbers rather than blanking. Keeping the previous
        // data on a key change is what makes that possible.
        placeholderData: <T,>(previous: T) => previous,
      },
      mutations: {
        // A mutation is a command. It is never retried silently: the operator sees the
        // failure, the form stays filled, and they decide.
        retry: false,
      },
    },
  });
}
